import React, { useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { bool, string } from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';

import { DATA_TEST_ID } from '~constants/tests';
import useDebounce from '~hooks/useDebounce';
import CartActions from '~redux/Cart/actions';
import { itemsPropTypes } from '~propTypes/items';
import { modifiersMapper, priceItem, removeItemFromCart } from '~utils/product';
import { useModifyProduct } from '~hooks/product';
import Text from '~components/Text';
import { formatPrice } from '~utils/number';
import { slugify } from '~utils/string';

import styles from './styles.module.scss';
import { DEBOUNCE_TIME } from './constants';

function OrderDetailsItem({ edition, item, closeProductModalUrl }) {
  const dispatch = useDispatch();
  const [isAddingOrSubstracting, setIsAddingOrSubstracting] = useState(false);
  const debouncedIsAOS = useDebounce(isAddingOrSubstracting, DEBOUNCE_TIME);
  const debouncedIsAOSAction = (callback, setValue) => {
    if (debouncedIsAOS === isAddingOrSubstracting) {
      callback();
      setValue(true);
    }
  };
  const {
    items,
    count,
    basePrice,
    category,
    product: { pictures, name }
  } = item;
  const plusOne = () =>
    debouncedIsAOSAction(() => dispatch(CartActions.sumItem(item)), setIsAddingOrSubstracting);
  const minusOne = () =>
    debouncedIsAOSAction(
      () => dispatch(CartActions.removeItem({ item, mapper: removeItemFromCart })),
      setIsAddingOrSubstracting
    );
  useEffect(() => setIsAddingOrSubstracting(false), [debouncedIsAOS]);
  const modifiers = useMemo(
    () =>
      modifiersMapper(items, (modifier) => ({
        name: modifier.name,
        quantity: modifier.quantity
      })),
    [items]
  );
  const price = useMemo(() => priceItem(items, count, basePrice), [basePrice, count, items]);
  const handleOpenProductDetailModal = useModifyProduct(closeProductModalUrl, item);
  const productName = slugify(`incart-${DATA_TEST_ID.productName}-${category}-${name}`);
  return (
    <div
      className={cn('column full-width space-between', styles.container, {
        [styles.detailContainer]: !edition
      })}
      data-testid={productName}>
      <div className={cn('column space-between', { 'full-width': !edition })}>
        <div className={cn('row space-between', { 'full-width': !edition })}>
          <div className="column full-width">
            <div className="row full-width space-between">
              <Text className={`text-emperor font-size-14 text-uppercase bold m-bottom-2 ${styles.title}`}>
                {name}
              </Text>
              {!edition && (
                <Text className="m-right-2">{i18next.t('OrderDetailsItem:quantity', { count })}</Text>
              )}
            </div>
            <div className="column">
              {modifiers.map((modifier, index) => (
                <Text
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${modifier}${index}`}
                  className={`text-dusty-gray light m-bottom-1 ${styles.ingredients}`}>
                  {`- ${modifier.name} x${modifier.quantity}`}
                </Text>
              ))}
            </div>
          </div>
          {edition && pictures?.[0]?.url && <img className={styles.image} src={pictures?.[0]?.url} />}
        </div>
      </div>
      {edition && (
        <>
          <div className="row middle space-between end m-top-2">
            <button
              type="button"
              onClick={handleOpenProductDetailModal}
              className="row bold text-brand-primary font-size-14">
              {i18next.t('OrderDetails:modify')}
            </button>
            <Text className="text-emperor end bold m-right-2 font-size-14">{formatPrice(price)}</Text>
            <div className={`${styles.countContainer} row middle`}>
              <button
                data-testid={DATA_TEST_ID.subtractQuantityProductButton}
                disabled={isAddingOrSubstracting}
                className={cn('m-right-2', {
                  [styles.disabledIcon]: isAddingOrSubstracting
                })}
                type="button"
                onClick={minusOne}>
                <span
                  className={cn('material-icons-outlined row middle center', styles.countButton, {
                    [styles.minus]: count > 1,
                    [styles.cross]: count === 1,
                    [styles.disabledIcon]: isAddingOrSubstracting
                  })}>
                  {count === 1 && 'close'}
                  {count > 1 && 'remove'}
                </span>
              </button>
              <Text className="m-right-2">{count}</Text>
              <button
                data-testid={DATA_TEST_ID.addQuantityProductButton}
                className={cn({
                  [styles.disabledIcon]: isAddingOrSubstracting
                })}
                disabled={isAddingOrSubstracting}
                type="button"
                onClick={plusOne}>
                <span
                  className={cn('material-icons-outlined row middle center', styles.countButton, {
                    [styles.disabledIcon]: isAddingOrSubstracting
                  })}>
                  add
                </span>
              </button>
            </div>
          </div>
          {item.instructions ? <div className={styles.instructions}>{item.instructions}</div> : null}
        </>
      )}
    </div>
  );
}

OrderDetailsItem.propTypes = {
  item: itemsPropTypes.isRequired,
  closeProductModalUrl: string,
  edition: bool
};

export default OrderDetailsItem;
